import { useEffect, useState } from 'react'
import { ShieldCheck, Sparkles } from 'lucide-react'

import { AdminBrandLogo, type BrandAssets } from '@/components/brand/AdminBrandLogo'
import { ShaderBackground } from '@/components/ui/shader-background'
import { SparkEffect } from '@/components/ui/spark-effect'
import { cn } from '@/lib/utils'

/**
 * Giriş shader’ı — lacivert → camgöbeği; kıvılcımlar üstte daha okunur kontrast için ayrı ayarlanıyor.
 */
const TENANT_SHADER = {
  lineColor: [0.35, 0.62, 1.0] as [number, number, number],
  bgColor1: [0.05, 0.08, 0.16] as [number, number, number],
  bgColor2: [0.08, 0.22, 0.34] as [number, number, number],
}

type Props = BrandAssets & {
  storeName: string
  title: string
  subtitle: string
  isDark?: boolean
  children: React.ReactNode
}

export function AnimatedLoginShell({
  storeName,
  logoLight,
  logoDark,
  iconLight,
  iconDark,
  isDark = false,
  title,
  subtitle,
  children,
}: Props) {
  const [reduceMotion, setReduceMotion] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setReduceMotion(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  return (
    <section className="relative flex min-h-svh flex-col overflow-hidden text-foreground">
      <div className="absolute inset-0 z-0 min-h-svh">
        {!reduceMotion ? (
          <>
            <ShaderBackground
              className="min-h-svh"
              lineColor={TENANT_SHADER.lineColor}
              bgColor1={TENANT_SHADER.bgColor1}
              bgColor2={TENANT_SHADER.bgColor2}
              timeScale={0.85}
            />
            <SparkEffect
              className="min-h-svh w-full"
              amount={4200}
              speed={0.058}
              lifetime={140}
              direction={{ x: -0.4, y: 1 }}
              size={[2, 4]}
              maxopacity={0.78}
              color="230,240,255"
              randColor={false}
              acceleration={[8, 42]}
            />
          </>
        ) : (
          <div
            className="min-h-svh w-full bg-gradient-to-br from-mainDark via-secondaryDark to-mainDark"
            aria-hidden
          />
        )}
      </div>

      <div
        className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-background/15 via-transparent to-background/30 dark:from-slate-950/20 dark:via-transparent dark:to-slate-950/40"
        aria-hidden
      />

      <header
        className={cn(
          'relative z-10 mx-4 mt-4 flex shrink-0 items-center justify-between gap-3 rounded-2xl border border-white/20 bg-white/12 px-4 py-3 shadow-[0_8px_32px_rgb(0_0_0/0.25)] backdrop-blur-xl sm:mx-6 sm:px-6 dark:border-white/12 dark:bg-slate-950/35',
          !reduceMotion && 'animate-in fade-in slide-in-from-top-3 duration-700',
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <AdminBrandLogo
            storeName={storeName}
            variant="logo"
            isDark={isDark}
            logoLight={logoLight}
            logoDark={logoDark}
            iconLight={iconLight}
            iconDark={iconDark}
            imgClassName="h-10 max-w-[140px] object-contain"
          />
          <div className="min-w-0">
            <p className="mt-0.5 flex items-center gap-1 text-[10px] font-medium tracking-wide text-white/55">
              <Sparkles className="h-3 w-3 shrink-0 text-amber-200/90" aria-hidden />
              <span className="truncate">Yönetim oturumu</span>
            </p>
          </div>
        </div>

        <div
          className={cn(
            'flex shrink-0 items-center gap-2 rounded-full border border-emerald-400/35 bg-emerald-500/15 px-3 py-2 text-xs font-semibold text-emerald-50 backdrop-blur-sm',
            !reduceMotion && 'login-auth-trust-pulse',
          )}
        >
          <ShieldCheck
            className={cn(
              'h-4 w-4 text-emerald-300',
              !reduceMotion && 'motion-safe:animate-[pulse_2s_ease-in-out_infinite]',
            )}
            aria-hidden
          />
          <span className="max-w-[10rem] truncate sm:max-w-none">Güvenli giriş</span>
        </div>
      </header>

      <div className="relative z-10 flex flex-1 items-center justify-center px-4 py-8 sm:py-10">
        <div
          className={cn(
            'w-full max-w-md rounded-2xl border border-white/20 bg-card/90 p-8 shadow-strong backdrop-blur-xl dark:border-white/15 dark:bg-secondaryDark/80',
            !reduceMotion && 'animate-in fade-in slide-in-from-bottom-4 duration-700',
          )}
        >
          <div className="mb-8 space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
            <p className="text-sm text-muted-foreground text-balance">{subtitle}</p>
          </div>
          {children}
        </div>
      </div>
    </section>
  )
}
